import { Injectable } from "@angular/core";

import { LocalStorage } from "@shared/enums";
import { KnownIssue, VersionDetails } from "@shared/models";

@Injectable({
  providedIn: "root",
})
export class AboutService {
  getVersions(): VersionDetails[] {
    return this.initializeData(LocalStorage.Versions, this.versions())
      .data as VersionDetails[];
  }

  getKnownIssues(): KnownIssue[] {
    return this.initializeData(LocalStorage.KnownIssues, this.knownIssues())
      .data as KnownIssue[];
  }

  private initializeData(
    key: LocalStorage,
    data: VersionDetails[] | KnownIssue[],
  ): { data: VersionDetails[] | KnownIssue[] } {
    const storedData = localStorage.getItem(key);
    if (!storedData || storedData.length !== data.length) {
      localStorage.setItem(key, JSON.stringify(data));
      return { data };
    } else {
      return { data: JSON.parse(storedData) };
    }
  }

  markAsViewed<T extends VersionDetails | KnownIssue>(
    item: T,
    key: LocalStorage,
  ): void {
    const storedData = localStorage.getItem(key);
    if (storedData) {
      const data = JSON.parse(storedData) as T[];
      const updatedData = data.map((d) => this.updateItem(d, item));
      localStorage.setItem(key, JSON.stringify(updatedData));
    }
  }

  private updateItem<T extends VersionDetails | KnownIssue>(
    storedItem: T,
    newItem: T,
  ): T {
    if (
      "version" in newItem &&
      "version" in storedItem &&
      storedItem.version === newItem.version
    ) {
      return { ...storedItem, isNew: false };
    } else if (
      "title" in newItem &&
      "title" in storedItem &&
      storedItem.title === newItem.title
    ) {
      return { ...storedItem, isNew: false };
    }
    return storedItem;
  }

  private versions(): VersionDetails[] {
    return [
      {
        version: "2.0.0",
        date: "12-04-2025",
        beta: false,
        description: "Initial release of Sequence History 2.0.",
        bugFixes: null,
        features: [
          "All changes to a sequence are now available.",
          "Specific changes are now highlighted.",
          "General UI simplified and improved.",
          "Ability to quickly jump to certain change types, such as RO, AF, and cancellations.",
        ],
        isNew: false,
        isShown: false,
      },
      {
        version: "2.1.0",
        date: "01-13-2026",
        beta: false,
        description: "Added RAP information to HSS where applicable.",
        bugFixes: null,
        features: [
          "Added support for HSS line: RSV FDP BEGAN XXXX/XX ENDS XXXX/XX.",
        ],
        isNew: false,
        isShown: false,
      },
      {
        version: "2.1.1",
        date: "02-02-2026",
        beta: false,
        description: "Addition of add codes as well as multiple bug fixes.",
        bugFixes: [
          "RSK line not red in 'colorized' mode.",
          "Occasionally sign-in time not displayed when expected.",
          "BLOCKED IOE now displayed correctly.",
          "Under certain conditions, NSHW would display on incorrect leg.",
        ],
        features: ["Add codes with clickable descriptions added."],
        isNew: false,
        isShown: false,
      },
      {
        version: "2.1.2",
        date: "02-25-2026",
        beta: false,
        description: "Remark column bug fixes.",
        bugFixes: [
          "Fixed AIR and RTD remarks not displaying.",
          "Multiple remarks will now stack vertically instead of horizontally to emulate the behavior of Sabre.",
        ],
        features: [],
        isNew: false,
        isShown: false,
      },
      {
        version: "2.1.3",
        date: "04-17-2026",
        beta: false,
        description: "Bug fix for timestamp timezone conversion.",
        bugFixes: [
          "Fixed bug where timestamp would not get converted to HBT correctly.",
        ],
        features: [],
        isNew: false,
        isShown: true,
      },
      {
        version: "2.1.4",
        date: "06-15-2026",
        beta: false,
        description:
          "Added 'actual seat' remark to HSS. Minor layout/formatting fixes.",
        bugFixes: ["Minor layout fixes."],
        features: [],
        isNew: false,
        isShown: true,
      },
      {
        version: "2.2.1",
        date: "06-30-2026",
        beta: false,
        description: "Added support for split duty sequences.",
        bugFixes: ["Actual seat comparison bug fixed."],
        features: ["Added support for split duty sequences."],
        isNew: false,
        isShown: true,
      },
      {
        version: "2.2.2",
        date: "09-24-2026",
        beta: false,
        description: "Added credit text to split duty sequences.",
        bugFixes: [],
        features: ["Added credit text to split duty sequences."],
        isNew: true,
        isShown: true,
      },
    ];
  }

  private knownIssues(): KnownIssue[] {
    return [
      // {
      //   title: "Change Log HSS",
      //   cause: "Unknown, investigation ongoing.",
      //   description:
      //     "Occasionally, an HSS in the change log may contain incomplete or incorrect data. This is primarily evident when viewing a change log hss that appears to be cut off on the left side.",
      //   isShown: false,
      //   isNew: true,
      // },
      // {
      //   title: "User Sequences Missing",
      //   cause: "Database issue linking sequence details of a removed sequence.",
      //   description:
      //     "Some sequences removed from a user's schedule are missing sequence details, causing the tool to omit them from the sequences section on the home page.",
      //   workaround:
      //     "Users can still search for the missing sequence by sequence number, date, and position to view the changes made to the sequence.",
      //   isShown: false,
      //   isNew: true,
      // },
    ];
  }
}
